import * as React from "react";
import { Linking, Pressable, ScrollView, Text, View } from "react-native";
import * as DocumentPicker from "expo-document-picker";
import * as ImagePicker from "expo-image-picker";
import { Link, Stack, usePathname, useRouter } from "expo-router";
import { zodResolver } from "@hookform/resolvers/zod";
import AsyncStorage from "@react-native-async-storage/async-storage";
import Fuse from "fuse.js";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  FilterIcon,
  FolderPlusIcon,
  GridIcon,
  HashIcon,
  ListIcon,
  MoreHorizontalIcon,
  SlidersIcon,
} from "lucide-react-native";
import { observer } from "mobx-react-lite";
import { Controller, useForm } from "react-hook-form";
import * as DropdownMenu from "zeego/dropdown-menu";

import {
  StackHeaderBack,
  StackHeaderRight,
  StackHeaderTitle,
} from "~/components/HeaderElements";
import { ResponsiveRenderer } from "~/components/ResponsiveRenderer";
import { CardDisplayMode, SaveCard } from "~/components/SaveCard";
import { SelectablePill } from "~/components/SelectablePill";
import { Filters } from "~/components/Workspace/Filters";
import {
  decodeFilterFromURL,
  encodeFilterForURL,
  FilterSchema,
  filterSchema,
  SAVE_TYPE_OPTIONS,
  setParams,
  useParams,
} from "~/components/Workspace/params";
import { useCurrentAccount } from "~/hooks/useCurrentAccount";
import { useCurrentSpace } from "~/hooks/useCurrentSpace";
import { icons, useHackyParams } from "~/lib/constants";
import { cn } from "~/lib/utils";
import { SaveFile, SaveMedia, SaveNote, SaveUrl } from "~/stores/space/store";
import { Button } from "~/ui/Button";
import { Dropdown } from "~/ui/Dropdown";
import { SearchInput } from "../../_layout";

function AllSaves() {
  const params = useParams();
  const { account, space } = useCurrentSpace();
  const { sort = "date-desc", filters = "", search = "" } = params;

  const { replace } = useRouter();

  const [view, setView] = React.useState<CardDisplayMode | null>("grid");

  React.useEffect(() => {
    // TODO: this should come from a user preference storage instead
    AsyncStorage.getItem("view").then((value) => {
      if (value) {
        setView(value as CardDisplayMode);
      } else {
        setView("grid");
      }
    });
  }, []);

  function getTitle() {
    if (filters) {
      return "Filtered Saves";
    }

    return "All Saves";
  }

  const saves = React.useMemo(() => {
    let saves = space.savesAsArray;

    if (filters) {
      const decodedFilters = decodeFilterFromURL(filters);
      if (decodedFilters) {
        saves = filterSaves(saves, decodedFilters);
      }
    }
    if (search) {
      saves = searchSaves(saves, search);
    }
    if (sort) {
      saves = sortSaves(saves, sort);
    }
    return saves;
  }, [filters, search, sort, space.savesAsArray]);

  if (!view) return null;

  return (
    <>
      <ResponsiveRenderer
        webDesktop={null}
        default={
          <Stack.Screen
            options={{
              headerLeft: () => (
                <StackHeaderBack
                  backPath={{
                    pathname:
                      "/(app)/(authenticated)/[account]/(space)/[space]",
                    params: {
                      account: account.slug,
                      space: space.slug,
                    },
                  }}
                />
              ),

              headerTitle: () => (
                <StackHeaderTitle>{getTitle()}</StackHeaderTitle>
              ),
              headerRight: () => (
                <StackHeaderRight>
                  <HeaderMenu />
                </StackHeaderRight>
              ),
            }}
          />
        }
      />

      <ResponsiveRenderer
        webDesktop={
          <View className="max-h-full grow overflow-auto">
            <View className="web:bg-surface-primary border-divider-strong flex-row items-start border-b px-5 py-4 web:sticky web:top-0 web:z-10">
              <View className="gap-2">
                <Text className="text-28 flex flex-row items-baseline gap-2 font-semibold text-white">
                  {getTitle()}
                </Text>
                <View className="flex-row items-center gap-2">
                  <Filters />
                  {(space.collections.size > 0 || space.tags.size > 0) && (
                    <React.Fragment>
                      <DropdownMenu.Root>
                        <DropdownMenu.Trigger asChild>
                          <Button
                            variant="outline"
                            size="small"
                            iconAfter={
                              <FilterIcon size={18} className="text-icon" />
                            }
                          >
                            <Text className="text-text-primary">Filter</Text>
                          </Button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Content>
                          <FilterButton />
                        </DropdownMenu.Content>
                      </DropdownMenu.Root>
                      {params.filters && (
                        <Link
                          href={{
                            pathname: `/(app)/(authenticated)/[account]/(space)/[space]/saves`,
                            params: {
                              ...params,
                              account: account.slug,
                              filters: "",
                            },
                          }}
                          asChild
                          replace
                        >
                          <Button variant="outline" size="small">
                            <Text className="text-text-primary">
                              Clear filters
                            </Text>
                          </Button>
                        </Link>
                      )}
                    </React.Fragment>
                  )}
                </View>
              </View>

              <View className="ml-auto flex-row gap-2">
                <View className="border-input-border flex-row gap-0 rounded-lg border">
                  <Button
                    variant="outline"
                    size="small"
                    className={cn(
                      "border-none",
                      view === "grid" ? "bg-button-secondary" : "",
                    )}
                    iconBefore={
                      <GridIcon className="text-text-primary" size={16} />
                    }
                    onPress={() => {
                      setView("grid");
                      AsyncStorage.setItem("view", "grid");
                    }}
                  />

                  <Button
                    variant="outline"
                    size="small"
                    className={cn(
                      "border-none",
                      view === "list" ? "bg-button-secondary" : "",
                    )}
                    iconBefore={
                      <ListIcon className="text-text-primary" size={16} />
                    }
                    onPress={() => {
                      setView("list");
                      AsyncStorage.setItem("view", "list");
                    }}
                  />
                </View>
                <Dropdown
                  asChild
                  trigger={
                    <Button
                      variant="outline"
                      size="small"
                      iconAfter={(() => {
                        switch (sort) {
                          case "date-asc":
                          case "title-asc":
                            return (
                              <ArrowUpIcon size={18} className="text-icon" />
                            );
                          case "date-desc":
                          case "title-desc":
                            return (
                              <ArrowDownIcon size={18} className="text-icon" />
                            );
                        }
                      })()}
                    >
                      <Text className="text-text-primary">
                        {(() => {
                          if (sort === "date-asc") return "By date";
                          if (sort === "date-desc") return "By date";
                          if (sort === "title-asc") return "By title (a to z)";
                          if (sort === "title-desc") return "By title (z to a)";
                        })()}
                      </Text>
                    </Button>
                  }
                  groups={[
                    {
                      title: "Date",
                      items: [
                        {
                          icon: {
                            ios: "arrow.down",
                            web: "arrow-down",
                          },
                          key: "Date Descending",
                          title: "By date",
                          onSelect() {
                            replace({
                              pathname: `/(app)/(authenticated)/[account]/(space)/[space]/saves`,
                              params: {
                                ...params,
                                account: account.slug,
                                space: space.slug,
                                sort: "date-desc",
                              },
                            });
                          },
                        },
                        {
                          icon: {
                            ios: "arrow.up",
                            web: "arrow-up",
                          },
                          key: "Date Ascending",
                          title: "By date",
                          onSelect() {
                            replace({
                              pathname: `/(app)/(authenticated)/[account]/(space)/[space]/saves`,
                              params: {
                                ...params,
                                account: account.slug,
                                space: space.slug,
                                sort: "date-asc",
                              },
                            });
                          },
                        },
                      ],
                    },

                    {
                      title: "title",
                      items: [
                        {
                          icon: {
                            ios: "arrow.down",
                            web: "arrow-down",
                          },
                          key: "title Descending",
                          title: "By title (z to a)",
                          onSelect() {
                            replace({
                              pathname: `/(app)/(authenticated)/[account]/(space)/[space]/saves`,
                              params: {
                                ...params,
                                account: account.slug,
                                space: space.slug,
                                sort: "title-desc",
                              },
                            });
                          },
                        },
                        {
                          icon: {
                            ios: "arrow.up",
                            web: "arrow-up",
                          },
                          key: "Title Ascending",
                          title: "By title (a to z)",
                          onSelect() {
                            replace({
                              pathname: `/(app)/(authenticated)/[account]/(space)/[space]/saves`,
                              params: {
                                ...params,
                                account: account.slug,
                                space: space.slug,
                                sort: "title-asc",
                              },
                            });
                          },
                        },
                      ],
                    },
                  ]}
                />
              </View>
            </View>
            <View className="gap-2 py-4">
              <Text className="text-text-tertiary px-4 uppercase">
                {saves.length} {saves.length === 1 ? "save" : "saves"}
              </Text>
              {view === "list" ? (
                <View className="gap-0">
                  {saves?.map((save) => (
                    <React.Fragment key={save.id}>
                      <SaveCard save={save} view={view} />
                      <View className="bg-grey-dark-3 h-px" />
                    </React.Fragment>
                  ))}
                </View>
              ) : (
                <View className="grid gap-4 px-4 web:grid-cols-[repeat(auto-fill,minmax(275px,1fr))]">
                  {saves?.map((save) => (
                    <SaveCard save={save} key={save.id} view={view} />
                  ))}
                </View>
              )}
            </View>
          </View>
        }
        default={
          <ScrollView>
            <View className="relative gap-4 px-4">
              <View className="flex-row items-center justify-center gap-2">
                <View className="flex-grow">
                  <SearchInput />
                </View>

                <Pressable className="border-input-border-active h-full max-h-full w-12 items-center justify-center rounded-md border p-2">
                  <SlidersIcon
                    size={18}
                    className="text-text-primary rotate-90"
                  />
                </Pressable>
              </View>

              <View className="gap-2">
                <Text className="text-text-tertiary px-4 uppercase">
                  {saves.length} {saves.length === 1 ? "save" : "saves"}
                </Text>
                <View className="grid grid-cols-2 gap-2">
                  {saves?.map((save) => (
                    <React.Fragment key={save.id}>
                      <SaveCard view={view} save={save} key={save.id} />
                      {view === "list" && (
                        <View className="bg-grey-dark-3 h-px" />
                      )}
                    </React.Fragment>
                  ))}
                </View>
              </View>
            </View>
          </ScrollView>
        }
      />
    </>
  );
}

function HeaderMenu() {
  const { push } = useRouter();
  const { slug } = useCurrentAccount();
  const { space } = useCurrentSpace();

  return (
    <>
      <Dropdown
        trigger={
          <Text className="text-white">
            <MoreHorizontalIcon size={24} />
          </Text>
        }
        groups={[
          {
            title: "select",
            items: [
              {
                icon: icons.select,
                title: "Select",
                key: "select",
                onSelect: () => {
                  setParams({ isSelecting: "true" });
                },
              },
            ],
          },
          {
            title: "add",
            items: [
              {
                key: "media",
                title: "Add Media",
                icon: icons.media,
                async onSelect() {
                  try {
                    await ImagePicker.requestMediaLibraryPermissionsAsync();
                    const result = await ImagePicker.launchImageLibraryAsync({
                      mediaTypes: ImagePicker.MediaTypeOptions.All,
                      presentationStyle:
                        ImagePicker.UIImagePickerPresentationStyle.FORM_SHEET,
                    });

                    if (!result.canceled) {
                      push({
                        pathname: "/(app)/(authenticated)/[account]/link",
                        params: {
                          account: slug,
                          type: "MEDIA",
                          asset: encodeURIComponent(
                            JSON.stringify(result.assets[0]),
                          ),
                          spaceId: space.id,
                        },
                      });
                    }
                  } catch (e) {
                    console.error(e);
                  }
                },
              },
              {
                key: "file",
                title: "Add File",
                icon: icons.file,

                async onSelect() {
                  try {
                    const result = await DocumentPicker.getDocumentAsync();

                    if (!result.canceled) {
                      push({
                        pathname: "/(app)/(authenticated)/[account]/link",
                        params: {
                          account: slug,
                          type: "FILE",
                          asset: encodeURIComponent(
                            JSON.stringify(result.assets[0]),
                          ),
                          spaceId: space.id,
                        },
                      });
                    }
                  } catch (e) {
                    console.error(e);
                  }
                },
              },
              {
                key: "link",
                title: "Add Link",
                icon: icons.link,
                onSelect() {},
              },
            ],
          },
          // {
          //   items: [
          //     {
          //       icon: {
          //         ios: "grid",
          //         web: "grid",
          //       },
          //       title: "Grid view",
          //       key: "grid-view",
          //       onSelect: () => {
          //         setParams({ view: "grid" });
          //       },
          //     },

          //     {
          //       icon: {
          //         ios: "list.bullet",
          //         web: "list",
          //       },
          //       title: "List view",
          //       key: "list-view",
          //       onSelect: () => {
          //         setParams({ view: "list" });
          //       },
          //     },
          //   ],
          // },
        ]}
      />
    </>
  );
}

export default observer(AllSaves);

function FilterButton() {
  const params = useHackyParams<{ filters: string }>();
  const { handleSubmit, control } = useForm<FilterSchema>({
    resolver: zodResolver(filterSchema),
    defaultValues: decodeFilterFromURL(params.filters) ?? {
      collection: {
        ids: [],
      },
      tag: {
        ids: [],
      },
    },
  });
  const { account, space } = useCurrentSpace();
  const pathname = usePathname();
  const { replace } = useRouter();

  // this is mostly useful when switch from singular filter type to multiple filter types
  function getDefaultMode(
    x:
      | FilterSchema["collection"]
      | FilterSchema["tag"]
      | FilterSchema["saveType"],
  ) {
    if (!x) return "is";
    if (x.ids.length > 1) {
      if (["is_any_of", "is_all_of", "is_none_of"].includes(x.mode)) {
        return x.mode;
      }
      return "is_any_of";
    }

    if (["is", "is_not"].includes(x.mode)) {
      return x.mode;
    }

    return "is";
  }

  const toRender: Array<React.ReactElement> = [];

  toRender.push(
    <View className="gap-2" key="saveType">
      <View className="flex-row items-baseline gap-1">
        <Text className="text-text-primary uppercase">Type</Text>
      </View>
      <View className="flex-row flex-wrap gap-2">
        <Controller
          control={control}
          name="saveType.ids"
          render={({ field }) => {
            return (
              <>
                {SAVE_TYPE_OPTIONS.map((s) => {
                  const isChecked = field.value?.includes(s.type);
                  return (
                    <SelectablePill
                      key={s.type}
                      onPress={() => {
                        const types = Array.isArray(field.value)
                          ? [...field.value]
                          : [];
                        const updatedTags = isChecked
                          ? types.filter((type) => type !== s.type)
                          : [...types, s.type];
                        field.onChange(updatedTags);
                      }}
                      isChecked={isChecked}
                      iconBefore={{ web: s.icon }}
                    >
                      {s.title}
                    </SelectablePill>
                  );
                })}
              </>
            );
          }}
        />
      </View>
    </View>,
  );

  if (space.collections.size > 0) {
    toRender.push(
      <View className="gap-2" key="collections">
        <View className="flex-row items-baseline gap-1">
          <Text className="text-text-primary uppercase">Collections</Text>
          <FolderPlusIcon className="text-text-primary" />
        </View>
        <View className="flex-row flex-wrap gap-2">
          <Controller
            control={control}
            name="collection.ids"
            render={({ field }) => {
              return (
                <>
                  {space.collectionsAsArray.map((collection) => {
                    const isChecked = field.value.includes(collection.id);
                    return (
                      <SelectablePill
                        key={collection.id}
                        onPress={() => {
                          const collections = Array.isArray(field.value)
                            ? [...field.value]
                            : [];
                          const updatedTags = isChecked
                            ? collections.filter(
                                (collectionId) =>
                                  collectionId !== collection.id,
                              )
                            : [...collections, collection.id];
                          field.onChange(updatedTags);
                        }}
                        isChecked={isChecked}
                      >
                        {collection.title ?? collection.slug}
                      </SelectablePill>
                    );
                  })}
                </>
              );
            }}
          />
        </View>
      </View>,
    );
  }

  if (space.tags.size > 0) {
    toRender.push(
      <View key="tags" className="gap-2">
        <View className="flex-row items-baseline gap-1">
          <Text className="text-text-primary uppercase">Tags</Text>
          <HashIcon className="text-text-primary" />
        </View>
        <View className="flex-row flex-wrap gap-2">
          <Controller
            control={control}
            name="tag.ids"
            render={({ field }) => (
              <>
                {space.tagsAsArray.map((tag) => {
                  const isChecked = field.value.includes(tag.id);
                  return (
                    <SelectablePill
                      isChecked={isChecked}
                      key={tag.id}
                      onPress={() => {
                        const tags = Array.isArray(field.value)
                          ? [...field.value]
                          : [];
                        const updatedTags = isChecked
                          ? tags.filter((tagId) => tagId !== tag.id)
                          : [...tags, tag.id];
                        field.onChange(updatedTags);
                      }}
                    >
                      {tag.value}
                    </SelectablePill>
                  );
                })}
              </>
            )}
          />
        </View>
      </View>,
    );
  }

  return (
    <View className="bg-surface-primary max-w-96 gap-4 rounded p-2">
      {toRender.map((x, i) => {
        if (i === toRender.length - 1) return x;
        return (
          <React.Fragment key={i}>
            {x}
            <View className="bg-grey-dark-3 h-px" />
          </React.Fragment>
        );
      })}

      <Button
        variant="primary"
        size="small"
        onPress={handleSubmit((data) => {
          replace({
            pathname: "/(app)/(authenticated)/[account]/(space)/[space]/saves",
            params: {
              account: account.slug,
              space: space.slug,
              filters: encodeFilterForURL({
                collection: {
                  ids: data.collection.ids,
                  mode: getDefaultMode(data.collection),
                },
                tag: {
                  ids: data.tag.ids,
                  mode: getDefaultMode(data.tag),
                },
                saveType: data.saveType
                  ? {
                      ids: data.saveType.ids,

                      mode: getDefaultMode(data.saveType) as Exclude<
                        FilterSchema["saveType"],
                        undefined
                      >["mode"],
                    }
                  : undefined,
              }),
            },
          });
        })}
      >
        Apply filter
      </Button>
      <Button
        variant="outline"
        size="small"
        onPress={() => {
          replace({
            pathname: "/(app)/(authenticated)/[account]/(space)/[space]/saves",
            params: {
              account: account.slug,
              space: space.slug,
            },
          });
        }}
      >
        Clear filter
      </Button>
    </View>
  );
}

function filterSaves(
  saves: Array<SaveUrl | SaveMedia | SaveFile | SaveNote>,
  filters: FilterSchema,
) {
  if (!filters) return saves;

  const filteredSaves = saves
    .filter((save) => {
      if (filters.collection.ids.length === 0) return true;
      switch (filters.collection.mode) {
        case "is":
          return save.collections.has(filters.collection.ids.at(0) ?? "");
        case "is_not":
          return !save.collections.has(filters.collection.ids.at(0) ?? "");
        case "is_all_of":
          return filters.collection.ids.every((collectionId) =>
            save.collections.has(collectionId),
          );
        case "is_any_of":
          return filters.collection.ids.some((collectionId) =>
            save.collections.has(collectionId),
          );
        case "is_none_of":
          return !filters.collection.ids.some((collectionId) =>
            save.collections.has(collectionId),
          );
        default:
          const _exhaustiveCheck: never = filters.collection.mode;
          return _exhaustiveCheck;
      }
    })
    .filter((save) => {
      if (filters.tag.ids.length === 0) return true;

      switch (filters.tag.mode) {
        case "is":
          return save.tags.has(filters.tag.ids.at(0) ?? "");
        case "is_not":
          return !save.tags.has(filters.tag.ids.at(0) ?? "");
        case "is_all_of":
          return filters.tag.ids.every((tagId) => save.tags.has(tagId));
        case "is_any_of":
          return filters.tag.ids.some((tagId) => save.tags.has(tagId));
        case "is_none_of":
          return !filters.tag.ids.some((tagId) => save.tags.has(tagId));
        default:
          const _exhaustiveCheck: never = filters.tag.mode;
          return _exhaustiveCheck;
      }
    })
    .filter((save) => {
      if (!filters.saveType) return true;
      if (filters.saveType?.ids.length === 0) return true;

      switch (filters.saveType.mode) {
        case "is":
          return filters.saveType.ids.includes(save.type);
        case "is_not":
          return !filters.saveType.ids.includes(save.type);
        case "is_any_of":
          return filters.saveType.ids.some((type) => save.type === type);
        case "is_none_of":
          return !filters.saveType.ids.some((type) => save.type === type);
        default:
          const _exhaustiveCheck: never = filters.saveType.mode;
          return _exhaustiveCheck;
      }
    });

  return filteredSaves;
}

function searchSaves(
  saves: Array<SaveUrl | SaveMedia | SaveFile | SaveNote>,
  search: string,
) {
  const fuse = new Fuse(saves, {
    keys: [
      "title",
      "description",
      "url",
      "meta.title",
      "meta.fileName",
      "meta.name",
      "richTextAsText",
    ],
    minMatchCharLength: 2,
  });
  return fuse.search(search).map((result) => result.item);
}

function sortSaves(
  saves: Array<SaveUrl | SaveMedia | SaveFile | SaveNote>,
  sort: "date-asc" | "date-desc" | "title-asc" | "title-desc",
) {
  return saves.toSorted((a, b) => {
    const titleA = a.title ?? "";
    const titleB = b.title ?? "";
    if (sort === "title-asc") {
      return titleA.localeCompare(titleB);
    }
    if (sort === "title-desc") {
      return titleB.localeCompare(titleA);
    }
    if (sort === "date-asc") {
      return a.createdAt > b.createdAt ? 1 : -1;
    }

    if (sort === "date-desc") {
      return a.createdAt < b.createdAt ? 1 : -1;
    }
    return 0;
  });
}
